import React, { useState } from 'react';
import {
  Printer,
  PackageCheck,
  Warehouse,
  Send,
  CheckCircle2,
  ShieldAlert,
  TrendingUp,
  Receipt,
  CreditCard,
  Scale
} from 'lucide-react';
import { EditorialProject } from '../../../../../types/EditorialProject.types';

const cn = (...args: any[]) => args.filter(Boolean).join(' ');

interface Phase4Props {
  project: EditorialProject;
  onUpdateProject: (updated: EditorialProject) => void;
}

export default function EditorialPhase4Closing({
  project,
  onUpdateProject
}: Phase4Props) {
  // Regla 3: no se puede emitir la OT sin la firma del Imprimátur
  const isImprimaturApproved = !!project.imprimatur?.approved;

  const [printShop, setPrintShop] = useState(project.printJob?.printShop || 'Marcial Pons Gráficas Industriales S.A.');
  const [contractedRun, setContractedRun] = useState(project.printJob?.contractedRun || project.tiradas[0]?.quantity || 2500);
  const [deliveredRun, setDeliveredRun] = useState(project.printJob?.effectiveDelivered || contractedRun);
  const [otNumber, setOtNumber] = useState(project.printJob?.otNumber || `OT-2026-${Math.floor(100 + Math.random() * 900)}`);
  const printStatus = project.printJob?.status || 'pendiente';

  const toleranceDelta = deliveredRun - contractedRun;
  const tolerancePct = contractedRun > 0 ? (toleranceDelta / contractedRun) * 100 : 0;
  const isWithinTolerance = Math.abs(tolerancePct) <= 5.0;

  const [copiesSoldInput, setCopiesSoldInput] = useState(project.authorLiquidation?.copiesSold || 1850);
  const [allowUnamortizedLiquidation, setAllowUnamortizedLiquidation] = useState(false);

  const advanceTotal = project.contractDetails?.advanceAmount || 0;
  const targetPvp = project.formats[0]?.price || 22.00;
  const netBasePerCopy = targetPvp / 1.04;

  const tier1Limit = project.contractDetails?.royaltyTiers[0]?.upToQty || 2000;
  const tier1Rate = (project.contractDetails?.royaltyTiers[0]?.rate || 8) / 100;
  const tier2Limit = project.contractDetails?.royaltyTiers[1]?.upToQty || 5000;
  const tier2Rate = (project.contractDetails?.royaltyTiers[1]?.rate || 10) / 100;
  const tier3Rate = (project.contractDetails?.royaltyTiers[2]?.rate || 12) / 100;

  let calculatedGrossRoyalties = 0;
  if (copiesSoldInput <= tier1Limit) {
    calculatedGrossRoyalties = copiesSoldInput * netBasePerCopy * tier1Rate;
  } else if (copiesSoldInput <= tier2Limit) {
    calculatedGrossRoyalties = tier1Limit * netBasePerCopy * tier1Rate + (copiesSoldInput - tier1Limit) * netBasePerCopy * tier2Rate;
  } else {
    calculatedGrossRoyalties = tier1Limit * netBasePerCopy * tier1Rate + (tier2Limit - tier1Limit) * netBasePerCopy * tier2Rate + (copiesSoldInput - tier2Limit) * netBasePerCopy * tier3Rate;
  }

  const advanceToAbsorb = Math.min(advanceTotal, calculatedGrossRoyalties);
  const remainingAdvance = Math.max(0, advanceTotal - calculatedGrossRoyalties);
  const netPayableToAuthor = Math.max(0, calculatedGrossRoyalties - advanceTotal);

  // Regla 4: no se puede liquidar royalties con anticipo sin amortizar, salvo autorización expresa
  const hasUnamortizedAdvance = remainingAdvance > 0;
  const canLiquidateRoyalties = !hasUnamortizedAdvance || allowUnamortizedLiquidation;

  const handleEmitWorkOrder = () => {
    if (!isImprimaturApproved) return;
    onUpdateProject({
      ...project,
      printJob: { otNumber, printShop, contractedRun, effectiveDelivered: deliveredRun, tolerancePercent: 5, otIssuedDate: new Date().toISOString().split('T')[0], status: 'ot_emitida' }
    });
  };

  const handleReceiveInWarehouse = () => {
    onUpdateProject({
      ...project,
      printJob: {
        ...(project.printJob || { otNumber, printShop, contractedRun, effectiveDelivered: deliveredRun, tolerancePercent: 5, status: 'pendiente' }),
        effectiveDelivered: deliveredRun,
        receiptInWarehouseId: `REC-ALB-${Math.floor(1000 + Math.random() * 9000)}`,
        status: 'recibido_almacen'
      }
    });
  };

  const handleLiquidateRoyalties = () => {
    if (!canLiquidateRoyalties) return;
    onUpdateProject({
      ...project,
      authorLiquidation: {
        period: 'Ejercicio Corriente (Liquidación Semestral)',
        copiesSold: copiesSoldInput,
        netRetailSales: Math.round(copiesSoldInput * netBasePerCopy),
        grossRoyalties: Math.round(calculatedGrossRoyalties),
        advanceToAmortize: Math.round(advanceToAbsorb),
        netPayable: Math.round(netPayableToAuthor),
        isLiquidated: true,
        liquidationDate: new Date().toISOString().split('T')[0],
        financeInvoiceGenerated: true
      },
      contractDetails: project.contractDetails ? { ...project.contractDetails, advanceAmortized: advanceToAbsorb } : undefined
    });
  };

  return (
    <div className="ed-grid-12">
      <div className="ed-col-5">
        <div className="ed-panel">
          <div className="ed-panel-header">
            <h3 className="ed-panel-title"><Printer size={14} style={{ color: 'var(--color-brand-green)' }} /> 5.1 Orden_De_Impresión (OT)</h3>
            <span className={cn('ed-badge', printStatus === 'recibido_almacen' ? 'ed-badge--green' : printStatus === 'ot_emitida' ? 'ed-badge--sky' : 'ed-badge--amber')}>
              {printStatus === 'recibido_almacen' ? 'ENTREGADA EN ALMACÉN' : printStatus === 'ot_emitida' ? 'EN IMPRENTA' : 'PENDIENTE OT'}
            </span>
          </div>

          {!isImprimaturApproved ? (
            <div className="ed-alert ed-alert--danger" style={{ marginBottom: '1rem' }}>
              <ShieldAlert size={16} />
              <div><strong>Bloqueo Estricto de Fase 3:</strong> No se puede emitir la Orden de Impresión (OT) a la imprenta sin la firma del Imprimátur (OK a Pruebas). Firme el Imprimátur en la Fase 3.</div>
            </div>
          ) : (
            <div className="ed-alert ed-alert--success" style={{ marginBottom: '1rem' }}>
              <CheckCircle2 size={13} />
              <span>Imprimátur validado por {project.imprimatur?.signedBy}. Autorizada emisión a Marcial Pons.</span>
            </div>
          )}

          <div className="ed-col-stack">
            <div className="ed-box">
              <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Imprenta Industrial Homologada</span>
              <select disabled={!isImprimaturApproved} value={printShop} onChange={(e) => setPrintShop(e.target.value)} className="ed-select">
                <option value="Marcial Pons Gráficas Industriales S.A.">Marcial Pons Gráficas Industriales S.A.</option>
                <option value="Gráficas Muriel S.A. (Offset/Digital)">Gráficas Muriel S.A. (Offset/Digital)</option>
                <option value="Taller de Artes Gráficas Huertas">Taller de Artes Gráficas Huertas</option>
              </select>
            </div>

            <div className="ed-grid-2">
              <div className="ed-box">
                <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Nº Orden de Trabajo (OT)</span>
                <input type="text" value={otNumber} onChange={(e) => setOtNumber(e.target.value)} className="ed-input" />
              </div>
              <div className="ed-box">
                <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Tirada Contratada</span>
                <input type="number" value={contractedRun} onChange={(e) => setContractedRun(Number(e.target.value))} className="ed-input" />
              </div>
            </div>

            <div className="ed-box">
              <div className="ed-row-between">
                <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <Scale size={12} style={{ color: '#38bdf8' }} /> Control de Tirada Efectiva
                </span>
                <span className={cn('ed-badge', isWithinTolerance ? 'ed-badge--green' : 'ed-badge--red')}>
                  Tolerancia: {tolerancePct >= 0 ? '+' : ''}{tolerancePct.toFixed(1)}% ({isWithinTolerance ? 'OK ±5%' : 'EXCEDE TOLERANCIA'})
                </span>
              </div>
              <div className="ed-field" style={{ marginTop: '0.5rem' }}>
                <label className="ed-field-label">Unidades Reales Entregadas por Imprenta:</label>
                <input type="number" value={deliveredRun} onChange={(e) => setDeliveredRun(Number(e.target.value))} className="ed-input" />
              </div>
              <p style={{ fontSize: '0.5rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                Diferencia: <strong>{toleranceDelta >= 0 ? `+${toleranceDelta}` : toleranceDelta} ejemplares</strong> respecto a la orden contratada.
              </p>
            </div>

            <div className="ed-connector">
              <span className="ed-connector-title" style={{ color: '#38bdf8' }}><Warehouse size={12} /> Conexión con Almacén / Logística</span>
              <p style={{ fontSize: '0.5rem', color: '#cbd5e1' }}>El cierre de la orden de impresión genera automáticamente una <strong>Orden de Recepción de Mercancía</strong> en el Almacén Central Marcial Pons.</p>
              {project.printJob?.receiptInWarehouseId && (
                <span style={{ fontSize: '0.5rem', color: 'var(--color-brand-green)', fontWeight: 700 }}>✓ Registrada Recepción: {project.printJob.receiptInWarehouseId}</span>
              )}
            </div>

            <div className="ed-col-stack">
              <button type="button" disabled={!isImprimaturApproved} onClick={handleEmitWorkOrder} className="ed-btn ed-btn--sky ed-btn--block">
                <Send size={12} /> Emitir Orden de Trabajo (OT) a Marcial Pons
              </button>
              <button type="button" onClick={handleReceiveInWarehouse} className="ed-btn ed-btn--primary ed-btn--block">
                <PackageCheck size={13} /> Confirmar Recepción en Almacén ({deliveredRun} Uds)
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ed-col-7">
        <div className="ed-panel">
          <div className="ed-panel-header">
            <h3 className="ed-panel-title"><Receipt size={14} style={{ color: 'var(--color-brand-green)' }} /> 5.2 Liquidación_De_Derechos_De_Autor (Royalties)</h3>
            <span className={cn('ed-badge', project.authorLiquidation?.isLiquidated ? 'ed-badge--green' : 'ed-badge--amber')}>
              {project.authorLiquidation?.isLiquidated ? 'LIQUIDACIÓN CERRADA' : 'LIQUIDACIÓN PENDIENTE'}
            </span>
          </div>

          <div className="ed-col-stack">
            <div className="ed-box">
              <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
                <TrendingUp size={12} style={{ color: '#38bdf8' }} /> Captura de Ventas Netas del Canal Comercial
              </span>
              <div className="ed-grid-3">
                <div className="ed-field">
                  <label className="ed-field-label">Ejemplares Vendidos Netos</label>
                  <input type="number" value={copiesSoldInput} onChange={(e) => setCopiesSoldInput(Number(e.target.value))} className="ed-input" />
                </div>
                <div className="ed-field">
                  <label className="ed-field-label">PVP de Venta al Público</label>
                  <div className="ed-input" style={{ display: 'flex' }}>{targetPvp.toFixed(2)} €</div>
                </div>
                <div className="ed-field">
                  <label className="ed-field-label">Base Imponible Devengada</label>
                  <div className="ed-input" style={{ display: 'flex' }}>{Math.round(copiesSoldInput * netBasePerCopy).toLocaleString()} €</div>
                </div>
              </div>
            </div>

            <div className="ed-box">
              <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: 'var(--color-brand-green)', textTransform: 'uppercase', display: 'block', marginBottom: '0.5rem' }}>Motor de Liquidación Escalonada</span>
              <div className="ed-col-stack" style={{ gap: '0.375rem' }}>
                <div className="ed-row-between" style={{ color: '#cbd5e1' }}>
                  <span>1. Royalties Brutos Devengados (según escalado de contrato):</span>
                  <strong style={{ color: '#fff' }}>{calculatedGrossRoyalties.toFixed(2)} €</strong>
                </div>
                <div className="ed-row-between" style={{ color: '#cbd5e1' }}>
                  <span>2. Anticipo Pagado a Compensar (Advance Inicial):</span>
                  <strong style={{ color: '#fbbf24' }}>{advanceTotal.toFixed(2)} €</strong>
                </div>
                <div className="ed-row-between" style={{ color: '#cbd5e1', borderTop: '1px solid #1e293b', paddingTop: '0.25rem' }}>
                  <span>3. Absorción de Anticipo en este Periodo (Unamortized Advance):</span>
                  <strong style={{ color: '#38bdf8' }}>-{advanceToAbsorb.toFixed(2)} €</strong>
                </div>
                <div className="ed-row-between" style={{ color: '#94a3b8', fontSize: '0.5rem' }}>
                  <span>Anticipo Restante por Amortizar en Siguientes Periodos:</span>
                  <span>{remainingAdvance.toFixed(2)} €</span>
                </div>
                <div className="ed-row-between" style={{ background: 'var(--color-brand-blue-light)', padding: '0.625rem', border: '1px solid var(--color-brand-blue-lighter)', marginTop: '0.25rem' }}>
                  <span style={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>Neto a Liquidar y Transferir al Autor:</span>
                  <span style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-brand-green)' }}>{netPayableToAuthor.toFixed(2)} €</span>
                </div>
              </div>
            </div>

            {hasUnamortizedAdvance && (
              <div className="ed-alert ed-alert--warning" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <ShieldAlert size={16} />
                  <div><strong>Control de Bloqueo Fase 4:</strong> El sistema no permite liquidar royalties a un autor si existen anticipos pendientes sin amortizar ({remainingAdvance.toFixed(2)}€ restantes), salvo configuración explícita del editor jefe.</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '0.25rem', borderTop: '1px solid rgba(245,158,11,0.2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={allowUnamortizedLiquidation} onChange={(e) => setAllowUnamortizedLiquidation(e.target.checked)} />
                  <span style={{ fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase' }}>Autorización Expresa del Editor Jefe para Liquidación con Saldo Negativo/Cero</span>
                </label>
              </div>
            )}

            <div className="ed-connector">
              <span className="ed-connector-title" style={{ color: '#38bdf8' }}><CreditCard size={12} /> Puntos de Conexión con Financiero</span>
              {project.modality === 'encargo' ? (
                <div style={{ color: '#c4b5fd' }}><strong>Proyecto por Encargo:</strong> La finalización de los hitos de impresión emite automáticamente las facturas de venta al cliente <strong>{project.clientName || 'Cliente'}</strong> según el plan de cobros acordado.</div>
              ) : (
                <div style={{ color: '#cbd5e1' }}><strong>Inversión Propia:</strong> La aprobación de la liquidación de autor genera automáticamente la factura de gasto por royalties a pagar en el módulo Financiero.</div>
              )}
            </div>

            <button type="button" disabled={!canLiquidateRoyalties} onClick={handleLiquidateRoyalties} className="ed-btn ed-btn--primary ed-btn--block">
              <Receipt size={14} /> Aprobar Liquidación y Generar Factura de Royalties
            </button>

            {project.authorLiquidation?.isLiquidated && (
              <div className="ed-box" style={{ background: '#0f0f0f' }}>
                <div className="ed-row-between" style={{ borderBottom: '1px solid #1e293b', paddingBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>Extracto Oficial de Liquidación para el Autor</span>
                  <span style={{ fontSize: '0.5rem', color: 'var(--color-brand-green)', fontWeight: 700 }}>EMITIDO</span>
                </div>
                <div className="ed-grid-2" style={{ marginTop: '0.5rem', color: '#cbd5e1' }}>
                  <div>Autor: <strong style={{ color: '#fff' }}>{project.bookAuthor}</strong></div>
                  <div>Fecha Liquidación: <strong style={{ color: '#fff' }}>{project.authorLiquidation.liquidationDate}</strong></div>
                  <div>Ejemplares Netos: <strong style={{ color: '#fff' }}>{project.authorLiquidation.copiesSold}</strong></div>
                  <div>Facturación Financiero: <strong style={{ color: 'var(--color-brand-green)' }}>✓ Generada</strong></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
